import { useEffect, useRef } from 'react'
import useRemoteParticipants from './useRemoteParticipants'
import { playAudio as playAudioFile, stopAudio } from '@/utils/audioPlayback'

const welcomeSpeechSound = 'https://assets.myjobglasses.com/immersion/phone-calls/welcome-speech.mp3'
const waitingSpeechSound = 'https://assets.myjobglasses.com/immersion/phone-calls/waiting-speech.mp3'
const waitingMusicSound = 'https://assets.myjobglasses.com/immersion/phone-calls/waiting-music.mp3'
const participantLeftSound = 'https://assets.myjobglasses.com/immersion/phone-calls/participant-left.mp3'
const WAITING_SPEECH_INTERVAL_MS = 30000
const PARTICIPANT_LEFT_INTERVAL_MS = 10000
const ROSTER_LOAD_GRACE_MS = 800
const MUSIC_BASE_VOLUME = 1.0
const MUSIC_DUCKED_VOLUME = 0.25
const VOLUME_FADE_DURATION_MS = 350
const VOLUME_FADE_STEP_MS = 25
const MUSIC_FADEOUT_DURATION_MS = 700
const MUSIC_GAP_MS = 500
const MUSIC_DURATION_MS = 15000

/**
 * Hook to run waiting-room phone audio sequence when no remote participants are present:
 * - Initial wait (no one was ever connected): plays welcome-speech, then waiting-music + waiting-speech loop
 * - Participant left (interlocutor disconnected): plays participant-left.mp3 every 10 seconds
 * - Stops immediately when a remote participant joins
 */
export function usePhoneWaitingSpeech(speakerDeviceId?: string) {
  const { hasRemoteParticipants } = useRemoteParticipants()
  const hasRemoteParticipantsRef = useRef(hasRemoteParticipants)
  const hadRemoteParticipantsRef = useRef(hasRemoteParticipants)
  const waitingSpeechIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const participantLeftTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const musicFadeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const musicReplayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const musicFadeOutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const musicFadeOutAudioRef = useRef<HTMLAudioElement | null>(null)
  const isWaitingSpeechPlayingRef = useRef(false)
  const musicAudioRef = useRef<HTMLAudioElement | null>(null)
  const sessionIdRef = useRef(0)

  useEffect(() => {
    hasRemoteParticipantsRef.current = hasRemoteParticipants
    if (hasRemoteParticipants) {
      hadRemoteParticipantsRef.current = true
    }

    const clearMusicFade = () => {
      if (musicFadeIntervalRef.current) {
        clearInterval(musicFadeIntervalRef.current)
        musicFadeIntervalRef.current = null
      }
    }

    const fadeMusicTo = (
      targetVolume: number,
      options?: {
        audioOverride?: HTMLAudioElement
        onComplete?: () => void
        durationMs?: number
      }
    ) => {
      const musicAudio = options?.audioOverride ?? musicAudioRef.current
      if (!musicAudio) {
        options?.onComplete?.()
        return
      }

      clearMusicFade()

      const durationMs = options?.durationMs ?? VOLUME_FADE_DURATION_MS
      const startVolume = musicAudio.volume
      const totalSteps = Math.max(1, Math.floor(durationMs / VOLUME_FADE_STEP_MS))
      const volumeStep = (targetVolume - startVolume) / totalSteps
      let step = 0

      musicFadeIntervalRef.current = setInterval(() => {
        if (!options?.audioOverride && hasRemoteParticipantsRef.current) {
          clearMusicFade()
          return
        }

        step += 1
        const nextVolume = step >= totalSteps ? targetVolume : musicAudio.volume + volumeStep
        musicAudio.volume = Math.max(0, Math.min(1, nextVolume))

        if (step >= totalSteps) {
          clearMusicFade()
          options?.onComplete?.()
        }
      }, VOLUME_FADE_STEP_MS)
    }

    const stopWaitingMusicWithFade = (): boolean => {
      const musicAudio = musicAudioRef.current
      if (!musicAudio) {
        return false
      }
      musicAudioRef.current = null
      musicFadeOutAudioRef.current = musicAudio
      fadeMusicTo(0, {
        audioOverride: musicAudio,
        durationMs: MUSIC_FADEOUT_DURATION_MS,
        onComplete: () => {
          musicFadeOutAudioRef.current = null
          stopAudio({ audioFile: waitingMusicSound, speakerDeviceId })
        }
      })
      return true
    }

    const stopWaitingAudio = () => {
      if (waitingSpeechIntervalRef.current) {
        clearInterval(waitingSpeechIntervalRef.current)
        waitingSpeechIntervalRef.current = null
      }
      if (participantLeftTimeoutRef.current) {
        clearTimeout(participantLeftTimeoutRef.current)
        participantLeftTimeoutRef.current = null
      }
      if (musicReplayTimeoutRef.current) {
        clearTimeout(musicReplayTimeoutRef.current)
        musicReplayTimeoutRef.current = null
      }
      if (musicFadeOutTimeoutRef.current) {
        clearTimeout(musicFadeOutTimeoutRef.current)
        musicFadeOutTimeoutRef.current = null
      }

      clearMusicFade()

      stopAudio({ audioFile: welcomeSpeechSound, speakerDeviceId })
      stopAudio({ audioFile: waitingSpeechSound, speakerDeviceId })
      const musicFadeStarted = stopWaitingMusicWithFade()
      if (!musicFadeStarted && musicFadeOutAudioRef.current) {
        stopAudio({ audioFile: waitingMusicSound, speakerDeviceId })
        musicFadeOutAudioRef.current = null
      }
      stopAudio({ audioFile: participantLeftSound, speakerDeviceId })

      isWaitingSpeechPlayingRef.current = false
    }

    const playWaitingSpeechWithDuckedMusic = async (sessionId: number) => {
      if (hasRemoteParticipantsRef.current || isWaitingSpeechPlayingRef.current) {
        return
      }

      isWaitingSpeechPlayingRef.current = true
      fadeMusicTo(MUSIC_DUCKED_VOLUME)

      try {
        const speechAudio = await playAudioFile({
          audioFile: waitingSpeechSound,
          speakerDeviceId,
          volume: 1.0
        })

        if (sessionIdRef.current !== sessionId || hasRemoteParticipantsRef.current) {
          stopAudio({ audioFile: waitingSpeechSound, speakerDeviceId })
          isWaitingSpeechPlayingRef.current = false
          return
        }

        const restoreMusicVolume = () => {
          isWaitingSpeechPlayingRef.current = false
          if (!hasRemoteParticipantsRef.current) {
            fadeMusicTo(MUSIC_BASE_VOLUME)
          }
        }

        speechAudio.addEventListener('ended', restoreMusicVolume, { once: true })
        speechAudio.addEventListener('error', restoreMusicVolume, { once: true })
      } catch (error) {
        console.warn('[PhoneWaitingSpeech] Could not play waiting speech:', error)
        isWaitingSpeechPlayingRef.current = false
        if (!hasRemoteParticipantsRef.current) {
          fadeMusicTo(MUSIC_BASE_VOLUME)
        }
      }
    }

    const runParticipantLeftSequence = async (sessionId: number) => {
      const playNext = async () => {
        if (hasRemoteParticipantsRef.current || sessionIdRef.current !== sessionId) {
          return
        }

        try {
          const audio = await playAudioFile({
            audioFile: participantLeftSound,
            speakerDeviceId,
            volume: 1.0
          })

          await new Promise<void>((resolve) => {
            const finish = () => resolve()
            audio.addEventListener('ended', finish, { once: true })
            audio.addEventListener('error', finish, { once: true })
          })
        } catch (error) {
          console.warn('[PhoneWaitingSpeech] Could not play participant-left:', error)
        }

        if (hasRemoteParticipantsRef.current || sessionIdRef.current !== sessionId) {
          return
        }

        participantLeftTimeoutRef.current = setTimeout(() => {
          participantLeftTimeoutRef.current = null
          void playNext()
        }, PARTICIPANT_LEFT_INTERVAL_MS)
      }

      void playNext()
    }

    const runWaitingSequence = async () => {
      const sessionId = sessionIdRef.current

      // Grace period: wait for roster to load before playing welcome speech.
      // Avoids hearing welcome speech when joining as second participant (interlocutor already online).
      await new Promise<void>((resolve) => setTimeout(resolve, ROSTER_LOAD_GRACE_MS))

      if (sessionIdRef.current !== sessionId || hasRemoteParticipantsRef.current) {
        return
      }

      try {
        const welcomeAudio = await playAudioFile({
          audioFile: welcomeSpeechSound,
          speakerDeviceId,
          volume: 1.0
        })

        await new Promise<void>((resolve) => {
          const finishWelcome = () => resolve()
          welcomeAudio.addEventListener('ended', finishWelcome, { once: true })
          welcomeAudio.addEventListener('error', finishWelcome, { once: true })
        })
      } catch (error) {
        console.warn('[PhoneWaitingSpeech] Could not play welcome speech:', error)
      }

      if (sessionIdRef.current !== sessionId || hasRemoteParticipantsRef.current) {
        return
      }

      const playWaitingMusicOnce = async () => {
        if (sessionIdRef.current !== sessionId || hasRemoteParticipantsRef.current) {
          return
        }

        try {
          const musicAudio = await playAudioFile({
            audioFile: waitingMusicSound,
            speakerDeviceId,
            volume: 0,
            loop: false
          })

          if (sessionIdRef.current !== sessionId || hasRemoteParticipantsRef.current) {
            stopAudio({ audioFile: waitingMusicSound, speakerDeviceId })
            return
          }

          musicAudioRef.current = musicAudio
          const targetVolume = isWaitingSpeechPlayingRef.current
            ? MUSIC_DUCKED_VOLUME
            : MUSIC_BASE_VOLUME
          fadeMusicTo(targetVolume)

          const scheduleNextPlay = () => {
            if (
              hasRemoteParticipantsRef.current ||
              sessionIdRef.current !== sessionId
            ) {
              return
            }
            musicReplayTimeoutRef.current = setTimeout(() => {
              musicReplayTimeoutRef.current = null
              void playWaitingMusicOnce()
            }, MUSIC_GAP_MS)
          }

          const startFadeOut = () => {
            const musicAudioForFade = musicAudioRef.current
            if (!musicAudioForFade) return

            musicAudioRef.current = null
            musicFadeOutAudioRef.current = musicAudioForFade
            fadeMusicTo(0, {
              audioOverride: musicAudioForFade,
              durationMs: MUSIC_FADEOUT_DURATION_MS,
              onComplete: () => {
                musicFadeOutAudioRef.current = null
                stopAudio({ audioFile: waitingMusicSound, speakerDeviceId })
                scheduleNextPlay()
              }
            })
          }

          musicFadeOutTimeoutRef.current = setTimeout(() => {
            musicFadeOutTimeoutRef.current = null
            if (sessionIdRef.current !== sessionId || hasRemoteParticipantsRef.current) {
              return
            }
            startFadeOut()
          }, MUSIC_DURATION_MS - MUSIC_FADEOUT_DURATION_MS)

          const onMusicEnded = () => {
            if (musicFadeOutTimeoutRef.current) {
              clearTimeout(musicFadeOutTimeoutRef.current)
              musicFadeOutTimeoutRef.current = null
              startFadeOut()
            }
          }
          musicAudio.addEventListener('ended', onMusicEnded, { once: true })
          musicAudio.addEventListener('error', onMusicEnded, { once: true })
        } catch (error) {
          console.warn('[PhoneWaitingSpeech] Could not play waiting music:', error)
        }
      }

      void playWaitingMusicOnce()

      waitingSpeechIntervalRef.current = setInterval(() => {
        void playWaitingSpeechWithDuckedMusic(sessionId)
      }, WAITING_SPEECH_INTERVAL_MS)
    }

    sessionIdRef.current += 1

    if (!hasRemoteParticipants) {
      if (hadRemoteParticipantsRef.current) {
        void runParticipantLeftSequence(sessionIdRef.current)
      } else {
        void runWaitingSequence()
      }
    } else {
      stopWaitingAudio()
    }

    return () => {
      sessionIdRef.current += 1
      stopWaitingAudio()
    }
  }, [hasRemoteParticipants, speakerDeviceId])
}
